import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  TimePicker,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  DollarOutlined,
  ShopOutlined
} from '@ant-design/icons';
import { dataEntryAPI, stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  station: {
    _id: string;
    name: string;
  };
  position: string;
  positionName?: string;
  salary: number;
  salaryType: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  isOnLeave: boolean;
}

const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState<any>({
    stationId: 'all',
    position: 'all',
    isActive: 'all'
  });
  const [form] = Form.useForm();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchEmployees();
  }, [filters]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.position !== 'all') params.position = filters.position;
      if (filters.isActive !== 'all') params.isActive = filters.isActive;

      const response = await dataEntryAPI.getEmployees(params);
      setEmployees(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Personel listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    form.resetFields();
    form.setFieldsValue({
      startDate: dayjs(),
      salaryType: 'monthly',
      isActive: true,
      isOnLeave: false
    });
    setModalVisible(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    form.setFieldsValue({
      stationId: employee.station._id,
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phone: employee.phone,
      position: employee.position,
      positionName: employee.positionName,
      salary: employee.salary,
      salaryType: employee.salaryType,
      startDate: dayjs(employee.startDate),
      endDate: employee.endDate ? dayjs(employee.endDate) : null,
      isActive: employee.isActive,
      isOnLeave: employee.isOnLeave
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.station,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        position: values.position,
        positionName: values.positionName,
        salary: values.salary || 0,
        salaryType: values.salaryType,
        startDate: values.startDate?.toISOString(),
        endDate: values.endDate?.toISOString() || null,
        isActive: values.isActive !== undefined ? values.isActive : true,
        isOnLeave: values.isOnLeave || false
      };

      if (editingEmployee) {
        await dataEntryAPI.updateEmployee(editingEmployee._id, data);
        message.success('Personel güncellendi');
      } else {
        await dataEntryAPI.addEmployee(data);
        message.success('Personel eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await dataEntryAPI.deleteEmployee(id);
      message.success('Personel silindi');
      fetchEmployees();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Silme işlemi başarısız');
    }
  };

  const positionLabels: any = {
    manager: 'Müdür',
    cashier: 'Kasiyer',
    pump_attendant: 'Pompa Görevlisi',
    technician: 'Teknisyen',
    security: 'Güvenlik',
    cleaner: 'Temizlik',
    other: 'Diğer'
  };

  const salaryTypeLabels: any = {
    monthly: 'Aylık',
    hourly: 'Saatlik',
    daily: 'Günlük',
    commission: 'Komisyon'
  };

  const columns = [
    {
      title: 'Ad Soyad',
      key: 'name',
      render: (record: Employee) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
    },
    {
      title: 'Pozisyon',
      dataIndex: 'position',
      key: 'position',
      render: (position: string, record: Employee) => (
        <Tag color="blue">
          {record.positionName || positionLabels[position] || position}
        </Tag>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Maaş',
      key: 'salary',
      render: (record: Employee) => (
        <span style={{ fontWeight: 'bold' }}>
          {record.salary.toLocaleString('tr-TR')} ₺ ({salaryTypeLabels[record.salaryType] || record.salaryType})
        </span>
      ),
    },
    {
      title: 'İşe Başlama',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (record: Employee) => (
        <Space>
          <Tag color={record.isActive ? 'success' : 'default'}>
            {record.isActive ? 'Aktif' : 'Pasif'}
          </Tag>
          {record.isOnLeave && <Tag color="warning">İzinli</Tag>}
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_: any, record: Employee) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditEmployee(record)}
          />
          <Popconfirm
            title="Bu personeli silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeEmployees = employees.filter(e => e.isActive);
  const totalSalary = activeEmployees.reduce((sum, e) => sum + (e.salary || 0), 0);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <UserOutlined /> Personel Yönetimi
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddEmployee} block={isMobile}>
            Yeni Personel Ekle
          </Button>
        </Col>
      </Row>

      {/* Filtreler */}
      <Card style={{ marginBottom: 24 }} styles={{ body: { padding: '16px' } }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8}>
            <Select
              value={filters.stationId}
              onChange={(value) => setFilters({ ...filters, stationId: value })}
              style={{ width: '100%' }}
              placeholder="İstasyon"
            >
              <Option value="all">Tüm İstasyonlar</Option>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              value={filters.position}
              onChange={(value) => setFilters({ ...filters, position: value })}
              style={{ width: '100%' }}
              placeholder="Pozisyon"
            >
              <Option value="all">Tüm Pozisyonlar</Option>
              <Option value="manager">Müdür</Option>
              <Option value="cashier">Kasiyer</Option>
              <Option value="pump_attendant">Pompa Görevlisi</Option>
              <Option value="technician">Teknisyen</Option>
              <Option value="security">Güvenlik</Option>
              <Option value="cleaner">Temizlik</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              value={filters.isActive}
              onChange={(value) => setFilters({ ...filters, isActive: value })}
              style={{ width: '100%' }}
              placeholder="Durum"
            >
              <Option value="all">Tümü</Option>
              <Option value="true">Aktif</Option>
              <Option value="false">Pasif</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Özet İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Personel"
              value={employees.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif Personel"
              value={activeEmployees.length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Maaş"
              value={totalSalary}
              prefix={<DollarOutlined />}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="İstasyon Sayısı"
              value={new Set(employees.map(e => e.station._id)).size}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>

      <Modal
        title={editingEmployee ? 'Personel Düzenle' : 'Yeni Personel Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingEmployee(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="station"
            label="İstasyon"
            rules={[{ required: true, message: 'İstasyon seçiniz' }]}
          >
            <Select placeholder="İstasyon seçin">
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Ad"
                rules={[{ required: true, message: 'Ad giriniz' }]}
              >
                <Input placeholder="Ad" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Soyad"
                rules={[{ required: true, message: 'Soyad giriniz' }]}
              >
                <Input placeholder="Soyad" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[{ required: true, message: 'Telefon giriniz' }]}
              >
                <Input placeholder="Telefon" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="E-posta"
              >
                <Input type="email" placeholder="E-posta (opsiyonel)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="position"
                label="Pozisyon"
                rules={[{ required: true, message: 'Pozisyon seçiniz' }]}
              >
                <Select placeholder="Pozisyon">
                  <Option value="manager">Müdür</Option>
                  <Option value="cashier">Kasiyer</Option>
                  <Option value="pump_attendant">Pompa Görevlisi</Option>
                  <Option value="technician">Teknisyen</Option>
                  <Option value="security">Güvenlik</Option>
                  <Option value="cleaner">Temizlik</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="positionName"
                label="Pozisyon Adı (Özel)"
              >
                <Input placeholder="Özel pozisyon adı (opsiyonel)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="salary"
                label="Maaş (₺)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={100}
                  placeholder="Maaş"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="salaryType"
                label="Maaş Tipi"
              >
                <Select>
                  <Option value="monthly">Aylık</Option>
                  <Option value="hourly">Saatlik</Option>
                  <Option value="daily">Günlük</Option>
                  <Option value="commission">Komisyon</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="İşe Başlama Tarihi"
                rules={[{ required: true, message: 'Tarih seçiniz' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="İşten Ayrılma Tarihi"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Aktif"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isOnLeave"
                label="İzinli"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingEmployee ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingEmployee(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Employees;

